import { EnvironmentProviders, Provider } from '@angular/core';

/**
 * A general interface for a provider feature that can be distinguished
 * by its `kind`.
 */
export interface ProviderFeature<TKind> {
  /** feature kind. */
  kind: TKind;
  /** Providers for this feature. */
  providers: Provider[];
}

/**
 * A general interface for an environment provider feature that can be
 * distinguished by its `kind`.
 */
export interface ProviderFeatureEnvironment<TKind> {
  kind: TKind;
  providers: EnvironmentProviders[];
}

/**
 * Use to create a common setup for providing additional features for providers.
 *
 * @example
 * enum MyFeatureKind { A, B }
 *
 * interface MyFeature<TKind extends MyFeatureKind> = ProviderFeature<TKind>;
 * const { make: makeMyFeature } = providerFeature<MyFeatureKind>();
 *
 * export function provideMyFeature(
 *  ...features: MyFeature<MyFeatureKind>[]
 * ) {...}
 *
 * export function withA(): MyFeature<MyFeatureKind.A> {
 *  const providers = [...];
 *  return makeMyFeature(MyFeatureKind.A, providers)
 * }
 */
export function providerFeature<TKinds>(): {
  make<TKind extends TKinds>(
    this: void,
    kind: TKind,
    providers: Provider[],
  ): ProviderFeature<TKind>;
} {
  return {
    // We directly return the function to create the provider feature(s)
    // This allows us to reuse the same function again and again while
    // at the same time improving type safety.
    make: makeProviderFeature,
  };
}

function makeProviderFeature<TKind>(
  kind: TKind,
  providers: Provider[],
): ProviderFeature<TKind> {
  return {
    kind,
    providers,
  };
}

/**
 * Use to create a common setup for providing additional features for providers.
 * This uses {@link EnvironmentProviders} to add features, so this can be used
 * for `EnvironmentInjector` e.g. Modules & Routes and can't be overwritten
 * within a Components `providers`
 *
 * @example
 * enum MyFeatureKind { A, B }
 *
 * interface MyFeature<TKind extends MyFeatureKind> = ProviderFeature<TKind>;
 * const { make: makeMyFeature } = providerFeature<MyFeatureKind>();
 *
 * export function provideMyFeature(
 *  ...features: MyFeature<MyFeatureKind>[]
 * ) {...}
 *
 * export function withA(): MyFeature<MyFeatureKind.A> {
 *  const providers = [...];
 *  return makeMyFeature(MyFeatureKind.A, providers)
 * }
 */
export function providerFeatureEnvironment<TKinds>(): {
  make<TKind extends TKinds>(
    this: void,
    kind: TKind,
    providers: EnvironmentProviders[],
  ): ProviderFeatureEnvironment<TKind>;
} {
  return {
    make: makeProviderFeatureEnvironment,
  };
}

function makeProviderFeatureEnvironment<TKind>(
  kind: TKind,
  providers: EnvironmentProviders[],
): ProviderFeatureEnvironment<TKind> {
  return {
    kind,
    providers,
  };
}
